/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Header, Footer } from "../components/Layout";
import api from "../lib/api";
import { CalendarBlank, ArrowLeft, User, BookOpen } from "@phosphor-icons/react";

// Public /blog/:slug detail page. Renders both local posts and
// rankai.ai articles (both arrive as markdown via the backend).

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

// Strip a leading `# Title` heading if it matches (or is suspiciously
// close to) the post's canonical title — both seeded posts and rankai
// articles often duplicate the H1, which then renders twice on the page.
function stripLeadingH1(md, title) {
  if (!md) return "";
  const lines = md.split("\n");
  // Find first non-blank line.
  let i = 0;
  while (i < lines.length && !lines[i].trim()) i += 1;
  if (i >= lines.length) return md;
  const m = lines[i].match(/^#\s+(.+)$/);
  if (!m) return md;
  const headingText = m[1].trim().toLowerCase();
  const titleText = (title || "").trim().toLowerCase();
  // Match if identical or one contains the other (catches em-dash /
  // subtitle differences like "Title — A Subtitle").
  if (
    headingText === titleText ||
    headingText.startsWith(titleText) ||
    titleText.startsWith(headingText)
  ) {
    lines[i] = "";
    return lines.join("\n");
  }
  return md;
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    api
      .get(`/blog/${slug}`)
      .then((res) => {
        if (!alive) return;
        setPost(res.data || null);
      })
      .catch((err) => {
        if (!alive) return;
        const status = err?.response?.status;
        if (status === 404) {
          setError("not_found");
        } else {
          setError("network");
        }
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [slug]);

  const canonical = `https://astrokuberchat.com/blog/${slug}`;
  const seoTitle = post?.seo_title || post?.title || "AstroKuberChat Blog";
  const seoDescription =
    post?.seo_description || post?.excerpt || "Vedic astrology guides from AstroKuberChat.";

  const jsonLd = post
    ? {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        description: seoDescription,
        image: post.featured_image_url || undefined,
        datePublished: post.published_at,
        dateModified: post.updated_at || post.published_at,
        mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
        author: {
          "@type": "Organization",
          name: post.author_name || "AstroKuberChat",
          url: "https://astrokuberchat.com",
        },
        publisher: {
          "@type": "Organization",
          name: "AstroKuberChat",
          logo: {
            "@type": "ImageObject",
            url: "https://astrokuberchat.com/logo.png",
          },
        },
      }
    : null;

  return (
    <div className="min-h-screen flex flex-col bg-[#FFFBF5]" data-testid="blog-post-page">
      <Helmet>
        <title>{`${seoTitle} — AstroKuberChat`}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={canonical} />
        {post?.featured_image_url ? (
          <meta property="og:image" content={post.featured_image_url} />
        ) : null}
        {jsonLd ? <script type="application/ld+json">{JSON.stringify(jsonLd)}</script> : null}
      </Helmet>

      <Header />

      <main className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-14 w-full">
        <button
          type="button"
          onClick={() => navigate("/blog")}
          className="mb-6 inline-flex items-center gap-2 text-sm text-stone-600 hover:text-amber-700 transition-colors"
          data-testid="blog-back-link"
        >
          <ArrowLeft size={16} /> All posts
        </button>

        {loading ? (
          <div className="space-y-4" data-testid="blog-post-loading">
            <div className="h-10 bg-stone-100 rounded animate-pulse w-3/4" />
            <div className="h-4 bg-stone-100 rounded animate-pulse w-1/2" />
            <div className="h-72 bg-stone-100 rounded-2xl animate-pulse mt-6" />
            <div className="h-4 bg-stone-100 rounded animate-pulse w-full mt-6" />
            <div className="h-4 bg-stone-100 rounded animate-pulse w-11/12" />
            <div className="h-4 bg-stone-100 rounded animate-pulse w-10/12" />
          </div>
        ) : error === "not_found" ? (
          <div className="text-center py-20" data-testid="blog-post-not-found">
            <BookOpen size={48} weight="duotone" className="mx-auto mb-4 text-stone-300" />
            <h1 className="font-serif-display text-3xl text-[#3F2D1B] mb-2">Post not found</h1>
            <p className="text-stone-600">
              That story has either moved or was never published.{" "}
              <Link to="/blog" className="text-amber-700 underline">
                Browse all posts →
              </Link>
            </p>
          </div>
        ) : error === "network" ? (
          <div className="text-center py-20 text-stone-500" data-testid="blog-post-error">
            <p>Couldn't load this post right now. Please try again.</p>
          </div>
        ) : post ? (
          <article data-testid="blog-post-article">
            <header className="mb-8">
              <div className="flex items-center gap-3 text-xs text-stone-500 mb-3">
                <span className="inline-flex items-center gap-1">
                  <CalendarBlank size={13} />
                  <time dateTime={post.published_at}>{formatDate(post.published_at)}</time>
                </span>
                <span aria-hidden>·</span>
                <span className="inline-flex items-center gap-1">
                  <User size={13} /> {post.author_name || "AstroKuberChat Team"}
                </span>
              </div>
              <h1
                className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#3F2D1B] leading-tight"
                data-testid="blog-post-title"
              >
                {post.title}
              </h1>
              {post.excerpt ? (
                <p className="mt-4 text-lg text-stone-600 leading-relaxed">{post.excerpt}</p>
              ) : null}
            </header>

            {post.featured_image_url ? (
              <div className="rounded-2xl overflow-hidden mb-10 bg-amber-50 aspect-[16/9]">
                <img
                  src={post.featured_image_url}
                  alt={post.title}
                  className="w-full h-full object-cover"
                  data-testid="blog-post-hero-image"
                />
              </div>
            ) : null}

            <div
              className="blog-prose prose prose-stone max-w-none prose-headings:font-serif-display prose-headings:text-[#3F2D1B] prose-a:text-amber-700 prose-a:no-underline hover:prose-a:underline prose-strong:text-[#3F2D1B] prose-img:rounded-xl"
              data-testid="blog-post-body"
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {stripLeadingH1(post.body_markdown || "", post.title)}
              </ReactMarkdown>
            </div>

            <div className="mt-14 pt-8 border-t border-amber-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="text-sm text-stone-500">
                Found this useful?{" "}
                <Link to="/astrologers" className="text-amber-700 underline">
                  Chat with a verified astrologer →
                </Link>
              </div>
              <Link
                to="/blog"
                className="text-sm font-medium text-amber-700 hover:text-amber-800"
                data-testid="blog-back-bottom"
              >
                ← All posts
              </Link>
            </div>
          </article>
        ) : null}
      </main>

      <Footer />
    </div>
  );
}
